import React, { useEffect } from "react";
import { Form, Checkbox, Radio, Input, Row, Col, message } from "antd";

const Section4 = ({ formValues, setFormValues, validationErrors, setValidationErrors }) => {
  // Gather all selected services from Section 3
  const selectedServices = [
    ...(formValues.creativeProduction || []),
    ...(formValues.digitalMarketing || []),
    ...(formValues.advertisingChannels || []),
    ...(formValues.publicEngagement || []),
    ...(formValues.otherServices?.trim() ? [formValues.otherServices.trim()] : []),
  ];

  // Remove duplicates and "Other" label
  const uniqueServices = Array.from(
    new Set(selectedServices.filter((s) => s !== "Other (please specify)"))
  ).sort((a, b) => a.localeCompare(b));

  const half = Math.ceil(uniqueServices.length / 2);
  const left = uniqueServices.slice(0, half);
  const right = uniqueServices.slice(half);

  // Handle checkbox change for core specialties
  const handleCoreSpecialtyChange = (checkedValues) => {
    if (checkedValues.length > 5) {
      message.warning("⚠️ You can select up to 5 core specialties only.");
      return;
    }
    setFormValues({ ...formValues, coreSpecialties: checkedValues });
  };

  // Validate that selected specialties are part of Section 3 services
  useEffect(() => {
    const invalidSelections = (formValues.coreSpecialties || []).filter(
      (specialty) => !selectedServices.includes(specialty)
    );

    setValidationErrors((prev) => ({
      ...prev,
      coreSpecialties: invalidSelections.length
        ? "Some selected specialties weren't chosen in Section 3. Please review."
        : null,
    }));
  }, [
    formValues.coreSpecialties,
    formValues.creativeProduction,
    formValues.digitalMarketing,
    formValues.advertisingChannels,
    formValues.publicEngagement,
    formValues.otherServices,
  ]);

  return (
    <Form layout="vertical">
      {/* Core Specialties */}
      {uniqueServices.length > 0 &&(
      <Form.Item label="17. Of the services you selected above, which 5 are your core specialties? (Select up to 5)">
        <Checkbox.Group
          value={formValues.coreSpecialties || []}
          onChange={handleCoreSpecialtyChange}
          style={{ width: "100%" }}
        >
          <Row gutter={[24, 8]}>
            <Col xs={24} md={12}>
              {left.map((label) => (
                <Checkbox key={label} value={label} style={{ display: "block", marginBottom: 8 }}>
                  {label}
                </Checkbox>
              ))}
            </Col>
            <Col xs={24} md={12}>
              {right.map((label) => (
                <Checkbox key={label} value={label} style={{ display: "block", marginBottom: 8 }}>
                  {label}
                </Checkbox>
              ))}
            </Col>
          </Row>
        </Checkbox.Group>

        {validationErrors?.coreSpecialties && (
          <div style={{ color: "red", fontWeight: "bold", marginTop: 8 }}>
            {validationErrors.coreSpecialties}
          </div>
        )}
      </Form.Item>
  )}

      {/* Other Core Specialty */}
      {formValues.coreSpecialties?.includes("Other (please specify)") && (
        <Form.Item label="Please specify your other specialty">
          <Input
            placeholder="Enter additional specialty"
            value={formValues.otherCoreSpecialty || ""}
            onChange={(e) =>
              setFormValues({ ...formValues, otherCoreSpecialty: e.target.value })
            }
          />
        </Form.Item>
      )}

      {/* Match Comfort */}
      {uniqueServices.length > 0 && (
        <>
         <Form.Item label="18. Would you be comfortable being matched primarily for the services you selected above?">
          <Radio.Group value={formValues.matchComfort}>
            <Radio
              value="Yes"
              onClick={() =>
                setFormValues({
                  ...formValues,
                  matchComfort:
                    formValues.matchComfort === "Yes"
                      ? undefined
                      : "Yes",
                })
              }
            >
              Yes
            </Radio>

            <Radio
              value="No"
              onClick={() =>
                setFormValues({
                  ...formValues,
                  matchComfort:
                    formValues.matchComfort === "No"
                      ? undefined
                      : "No",
                })
              }
            >
              No
            </Radio>

            <Radio
              value="Depends"
              onClick={() =>
                setFormValues({
                  ...formValues,
                  matchComfort:
                    formValues.matchComfort === "Depends"
                      ? undefined
                      : "Depends",
                })
              }
            >
              Depends (please explain)
            </Radio>
          </Radio.Group>
      </Form.Item>

      {formValues.matchComfort === "Depends" && (
        <Form.Item label="Please explain your answer">
          <Input.TextArea
            placeholder="Explain under what conditions you’d accept being matched."
            value={formValues.matchComfortExplanation || ""}
            onChange={(e) =>
              setFormValues({ ...formValues, matchComfortExplanation: e.target.value })
            }
          />
        </Form.Item>
      )}
      </>
      )}  

      {/* Campaign Execution */}
      <Form.Item label="19. Do you manage full campaign execution, or only place media buys?">
       <Radio.Group value={formValues.campaignExecution}>
            <Radio
              value="Full"
              onClick={() =>
                setFormValues({
                  ...formValues,
                  campaignExecution:
                    formValues.campaignExecution === "Full"
                      ? undefined
                      : "Full",
                })
              }
            >
              We manage full campaign execution
            </Radio>

            <Radio
              value="Media Buys Only"
              onClick={() =>
                setFormValues({
                  ...formValues,
                  campaignExecution:
                    formValues.campaignExecution === "Media Buys Only"
                      ? undefined
                      : "Media Buys Only",
                })
              }
            >
              We primarily place media buys
            </Radio>

            <Radio
              value="Both"
              onClick={() =>
                setFormValues({
                  ...formValues,
                  campaignExecution:
                    formValues.campaignExecution === "Both"
                      ? undefined
                      : "Both",
                })
              }
            >
              We do both depending on the project
            </Radio>

            <Radio
              value="Neither"
              onClick={() =>
                setFormValues({
                  ...formValues,
                  campaignExecution:
                    formValues.campaignExecution === "Neither"
                      ? undefined
                      : "Neither",
                })
              }
            >
              Neither
            </Radio>
          </Radio.Group>
      </Form.Item>

      {/* Campaign Lead Time */}
      <Form.Item label="20. What is your average campaign lead time from intake to launch?">
        <Radio.Group value={formValues.leadTime}>
          <Radio
            value="Less than 1 week"
            onClick={() =>
              setFormValues({
                ...formValues,
                leadTime:
                  formValues.leadTime === "Less than 1 week"
                    ? undefined
                    : "Less than 1 week",
              })
            }
          >
            Less than 1 week
          </Radio>

          <Radio
            value="1-2 weeks"
            onClick={() =>
              setFormValues({
                ...formValues,
                leadTime:
                  formValues.leadTime === "1-2 weeks"
                    ? undefined
                    : "1-2 weeks",
              })
            }
          >
            1-2 weeks
          </Radio>

          <Radio
            value="3-4 weeks"
            onClick={() =>
              setFormValues({
                ...formValues,
                leadTime:
                  formValues.leadTime === "3-4 weeks"
                    ? undefined
                    : "3-4 weeks",
              })
            }
          >
            3-4 weeks
          </Radio>

          <Radio
            value="1-2 months"
            onClick={() =>
              setFormValues({
                ...formValues,
                leadTime:
                  formValues.leadTime === "1-2 months"
                    ? undefined
                    : "1-2 months",
              })
            }
          >
            1-2 months
          </Radio>

          <Radio
            value="More than 2 months"
            onClick={() =>
              setFormValues({
                ...formValues,
                leadTime:
                  formValues.leadTime === "More than 2 months"
                    ? undefined
                    : "More than 2 months",
              })
            }
          >
            More than 2 months
          </Radio>

          <Radio
            value="Varies significantly"
            onClick={() =>
              setFormValues({
                ...formValues,
                leadTime:
                  formValues.leadTime === "Varies significantly"
                    ? undefined
                    : "Varies significantly",
              })
            }
          >
            It varies significantly by project
          </Radio>

          <Radio
            value="Not sure"
            onClick={() =>
              setFormValues({
                ...formValues,
                leadTime:
                  formValues.leadTime === "Not sure"
                    ? undefined
                    : "Not sure",
              })
            }
          >
            Not sure / we don't manage campaign launches
          </Radio>
        </Radio.Group>
      </Form.Item>
    </Form>
  );
};

export default Section4;
